import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Package, Briefcase, User, LogOut, ShoppingBag, Calendar,
  CheckCircle2, Clock, Truck, XCircle, MapPin,
} from "lucide-react";

const STATUS_META = {
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-800", Icon: Clock },
  confirmed: { label: "Confirmed", cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  preparing: { label: "Preparing", cls: "bg-emerald-100 text-emerald-800", Icon: Package },
  ready: { label: "Ready", cls: "bg-blue-100 text-blue-800", Icon: Package },
  out_for_delivery: { label: "Out for delivery", cls: "bg-blue-100 text-blue-800", Icon: Truck },
  delivered: { label: "Delivered", cls: "bg-emerald-200 text-emerald-900", Icon: CheckCircle2 },
  cancelled: { label: "Cancelled", cls: "bg-red-100 text-red-800", Icon: XCircle },
  received: { label: "Received", cls: "bg-amber-100 text-amber-800", Icon: Clock },
  reviewing: { label: "Under review", cls: "bg-blue-100 text-blue-800", Icon: Clock },
  interview: { label: "Interview stage", cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  offered: { label: "Offered", cls: "bg-emerald-200 text-emerald-900", Icon: CheckCircle2 },
  rejected: { label: "Not selected", cls: "bg-gray-100 text-gray-700", Icon: XCircle },
  hired: { label: "Hired", cls: "bg-emerald-500 text-white", Icon: CheckCircle2 },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status || "unknown", cls: "bg-gray-100 text-gray-700", Icon: Clock };
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-semibold ${m.cls}`}>
      <Icon size={11} /> {m.label}
    </span>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/public/customers/orders");
        if (!cancelled) setOrders(data);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="text-sm text-gray-500 py-8 text-center">Loading orders…</div>;
  if (orders.length === 0) return (
    <Card><CardContent className="py-12 text-center">
      <ShoppingBag size={36} className="mx-auto text-gray-300 mb-3" />
      <div className="font-semibold text-gray-900">No orders yet</div>
      <p className="text-sm text-gray-600 mt-1">Your online orders will appear here.</p>
      <Link to="/site/shop">
        <Button className="mt-4 bg-emerald-900 hover:bg-emerald-950">Start shopping</Button>
      </Link>
    </CardContent></Card>
  );

  return (
    <div className="space-y-3">
      {orders.map((o) => (
        <Card key={o.id} data-testid={`order-${o.order_no}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-display font-semibold text-gray-900">
                  {o.order_no}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                  <Calendar size={11} /> {new Date(o.created_at).toLocaleString()}
                </div>
              </div>
              <StatusBadge status={o.status} />
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-700">
              <div className="font-medium">{(o.items || []).length} item(s)</div>
              <ul className="mt-1.5 space-y-1">
                {(o.items || []).slice(0, 4).map((it, i) => (
                  <li key={i} className="text-xs text-gray-600 flex justify-between">
                    <span>{it.quantity}× {it.name}</span>
                    <span>{o.currency} {Number(it.line_total || 0).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
              <div className="flex justify-between mt-3 pt-2 border-t border-gray-100 font-semibold">
                <span>Total</span>
                <span className="text-emerald-900">{o.currency} {Number(o.total || 0).toLocaleString()}</span>
              </div>
              {o.delivery_address && (
                <div className="mt-2 flex items-start gap-1 text-xs text-gray-500">
                  <MapPin size={11} className="mt-0.5" /> {o.delivery_address}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ApplicationsTab() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/public/customers/applications");
        if (!cancelled) setApps(data);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="text-sm text-gray-500 py-8 text-center">Loading applications…</div>;
  if (apps.length === 0) return (
    <Card><CardContent className="py-12 text-center">
      <Briefcase size={36} className="mx-auto text-gray-300 mb-3" />
      <div className="font-semibold text-gray-900">No applications yet</div>
      <p className="text-sm text-gray-600 mt-1">Applied jobs will appear here so you can track them.</p>
      <Link to="/site/careers">
        <Button className="mt-4 bg-emerald-900 hover:bg-emerald-950">Browse open jobs</Button>
      </Link>
    </CardContent></Card>
  );

  return (
    <div className="space-y-3">
      {apps.map((a) => (
        <Card key={a.id} data-testid={`application-${a.id}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-display font-semibold text-gray-900">
                  {a.career_title || "Job position"}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {a.career_location && <>📍 {a.career_location} · </>}
                  Applied {new Date(a.created_at).toLocaleDateString()}
                </div>
              </div>
              <StatusBadge status={a.status} />
            </div>
            {a.cover_letter && (
              <div className="mt-2 pt-2 border-t border-gray-100">
                <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">Your cover letter</div>
                <div className="text-sm text-gray-700 line-clamp-3">{a.cover_letter}</div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ProfileTab() {
  const { customer, updateProfile, logout } = useCustomerAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    full_name: customer?.full_name || "",
    phone: customer?.phone || "",
    delivery_address: customer?.delivery_address || "",
    preferred_branch: customer?.preferred_branch || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateProfile(form);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const doLogout = async () => {
    await logout();
    nav("/site");
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6 space-y-3">
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label className="text-xs">Full name</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input value={customer?.email || ""} disabled />
              <div className="text-[11px] text-gray-500 mt-1">Email cannot be changed. Contact us if needed.</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Preferred branch</Label>
                <Input value={form.preferred_branch} onChange={(e) => setForm({ ...form, preferred_branch: e.target.value })} placeholder="Main Abrah" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Default delivery address</Label>
              <Input value={form.delivery_address} onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} />
            </div>
            <Button type="submit" disabled={busy} className="bg-emerald-900 hover:bg-emerald-950" data-testid="profile-save">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </form>
        </CardContent>
      </Card>
      <Card className="border-red-200">
        <CardContent className="py-4 flex items-center justify-between">
          <div>
            <div className="font-semibold text-gray-900">Sign out</div>
            <div className="text-xs text-gray-600">You&apos;ll stay signed in on this device until you sign out.</div>
          </div>
          <Button variant="outline" onClick={doLogout} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="account-logout">
            <LogOut size={14} className="mr-1.5" /> Sign out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default function PublicAccount() {
  const { customer, loading } = useCustomerAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!loading && !customer) nav("/site/login", { replace: true, state: { next: "/site/account" } });
  }, [customer, loading, nav]);

  if (loading || !customer) return <div className="max-w-4xl mx-auto p-8 text-sm text-gray-500">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-6 flex items-center gap-3">
        <div className="h-12 w-12 rounded-full bg-emerald-900 text-white flex items-center justify-center font-display font-bold text-lg">
          {customer.full_name?.[0]?.toUpperCase() || "A"}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">My account</div>
          <h1 className="font-display text-2xl font-bold text-gray-900">
            Hi, {customer.full_name.split(" ")[0]}
          </h1>
        </div>
        <Badge className="ml-auto bg-emerald-100 text-emerald-800">Customer</Badge>
      </div>

      <Tabs defaultValue="orders">
        <TabsList className="mb-4">
          <TabsTrigger value="orders" data-testid="tab-orders"><Package size={13} className="mr-1.5" />My orders</TabsTrigger>
          <TabsTrigger value="applications" data-testid="tab-applications"><Briefcase size={13} className="mr-1.5" />Applications</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile"><User size={13} className="mr-1.5" />Profile</TabsTrigger>
        </TabsList>
        <TabsContent value="orders"><OrdersTab /></TabsContent>
        <TabsContent value="applications"><ApplicationsTab /></TabsContent>
        <TabsContent value="profile"><ProfileTab /></TabsContent>
      </Tabs>
    </div>
  );
}
